/* eslint-disable react/destructuring-assignment, no-unused-vars, no-lonely-if */
import { $getSelection, $isRangeSelection, $isTextNode, $createTextNode } from 'lexical';
import { $createStyledTextNode, $isStyledTextNode } from './StyledTextNode';

/**
 * Get the style property from a text node (works with both TextNode and StyledTextNode)
 */
// eslint-disable-next-line react/function-component-definition
export function $getNodeStyle(node, property) {
  if ($isStyledTextNode(node)) {
    const style = node.getRichStyle();
    return style[property];
  }
  return null;
}

/**
 * Get all styled text nodes in the current selection
 */
export function $getStyledTextNodesInSelection() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return [];
  }

  const nodes = selection.getNodes();
  return nodes.filter((node) => $isTextNode(node));
}

/**
 * Check if any node in the selection has a specific style property
 */
export function $hasStyleInSelection(property) {
  const nodes = $getStyledTextNodesInSelection();
  return nodes.some((node) => $getNodeStyle(node, property) !== null);
}

/**
 * Get all values of a specific style property in the selection
 */
export function $getStyleValuesInSelection(property) {
  const nodes = $getStyledTextNodesInSelection();
  const values = nodes
    .map((node) => $getNodeStyle(node, property))
    .filter((value) => value !== null);
  return values;
}

/**
 * Get the most common value for a style property in the selection
 */
// eslint-disable-next-line react/function-component-definition
export function $getMostCommonStyleValue(property) {
  const values = $getStyleValuesInSelection(property);
  if (values.length === 0) {
    return null;
  }

  const counts = {};
  values.forEach((value) => {
    counts[value] = (counts[value] || 0) + 1;
  });

  return Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b));
}

/**
 * Convert a regular TextNode to a StyledTextNode
 */
export function $convertToStyledTextNode(node, style = {}) {
  if (!$isTextNode(node)) {
    return node;
  }

  if ($isStyledTextNode(node)) {
    // Already a StyledTextNode, just update the style
    node.setStyle(style);
    return node;
  }

  // Create a new StyledTextNode with the same text and format
  const styledNode = $createStyledTextNode(node.getTextContent(), style);
  styledNode.setFormat(node.getFormat());
  styledNode.setDetail(node.getDetail());
  styledNode.setMode(node.getMode());

  // Replace the original node
  node.replace(styledNode);
  return styledNode;
}

/**
 * Apply a style to a text node with specific offsets
 * Returns the styled node that was created/modified
 */
const $applyStyleToNodeWithOffsets = (node, startOffset, endOffset, styleProperty, styleValue) => {
  if (!$isTextNode(node)) {
    return null;
  }

  const text = node.getTextContent();
  const textLength = text.length;

  // Validate offsets
  if (startOffset < 0 || endOffset > textLength || startOffset >= endOffset) {
    return null;
  }

  // If the entire node is selected, apply the style directly
  if (startOffset === 0 && endOffset === textLength) {
    const existingStyle = $isStyledTextNode(node) ? node.getRichStyle() : {};
    const newStyle = { ...existingStyle, [styleProperty]: styleValue };

    if ($isStyledTextNode(node)) {
      const writable = node.getWritable();
      writable.__richStyle = newStyle;
      return writable;
    }
    const styledNode = $convertToStyledTextNode(node, newStyle);
    return styledNode;
  }

  // Partial selection - need to split the node
  const beforeText = text.slice(0, startOffset);
  const selectedText = text.slice(startOffset, endOffset);
  const afterText = text.slice(endOffset);

  const existingStyle = $isStyledTextNode(node) ? node.getRichStyle() : {};
  const format = node.getFormat();
  const detail = node.getDetail();
  const mode = node.getMode();

  // Build array of new nodes to replace the original
  const newNodes = [];

  // Create the before node if needed
  if (beforeText) {
    const beforeNode = $isStyledTextNode(node)
      ? $createStyledTextNode(beforeText, existingStyle)
      : $createTextNode(beforeText);
    beforeNode.setFormat(format);
    beforeNode.setDetail(detail);
    beforeNode.setMode(mode);
    newNodes.push(beforeNode);
  }

  // Create the styled selected node
  const newStyle = { ...existingStyle, [styleProperty]: styleValue };
  const selectedNode = $createStyledTextNode(selectedText, newStyle);
  selectedNode.setFormat(format);
  selectedNode.setDetail(detail);
  selectedNode.setMode(mode);
  newNodes.push(selectedNode);

  // Create the after node if needed
  if (afterText) {
    const afterNode = $isStyledTextNode(node)
      ? $createStyledTextNode(afterText, existingStyle)
      : $createTextNode(afterText);
    afterNode.setFormat(format);
    afterNode.setDetail(detail);
    afterNode.setMode(mode);
    newNodes.push(afterNode);
  }

  // Get the parent to insert nodes correctly
  const parent = node.getParent();
  if (!parent) {
    return null;
  }

  // Insert all new nodes in order before removing the original
  newNodes.forEach((newNode) => {
    node.insertBefore(newNode);
  });

  // Remove the original node
  node.remove();

  // Return the styled node (which is in the middle of newNodes)
  return selectedNode;
};

/**
 * Apply a style to a text node (partial selection support)
 * @deprecated Use $applyStyleToNodeWithOffsets instead
 */
export function $applyStyleToNode(node, selection, styleProperty, styleValue) {
  if (!$isTextNode(node)) {
    return false;
  }

  const text = node.getTextContent();
  const textLength = text.length;

  // Get the actual text nodes from selection anchors
  let anchorNode = selection.anchor.getNode();
  let focusNode = selection.focus.getNode();

  const originalAnchorNode = anchorNode;
  const originalFocusNode = focusNode;

  // Handle cases where anchor/focus might be on element nodes
  if (!$isTextNode(anchorNode) && anchorNode.getChildrenSize() > 0) {
    anchorNode = anchorNode.getFirstChild();
  }
  if (!$isTextNode(focusNode) && focusNode.getChildrenSize() > 0) {
    focusNode = focusNode.getFirstChild();
  }

  // Determine if this is the anchor or focus node
  const nodeKey = node.getKey();
  const isAnchorNode = nodeKey === anchorNode?.getKey();
  const isFocusNode = nodeKey === focusNode?.getKey();

  // Get offsets
  let startOffset = 0;
  let endOffset = textLength;

  if (isAnchorNode && isFocusNode) {
    // Single node selection
    const isBackward = selection.isBackward();
    startOffset = Math.min(selection.anchor.offset, selection.focus.offset);
    endOffset = Math.max(selection.anchor.offset, selection.focus.offset);
  } else if (isAnchorNode) {
    // First node in multi-node selection
    startOffset = selection.anchor.offset;
    endOffset = textLength;
  } else if (isFocusNode) {
    // Last node in multi-node selection
    startOffset = 0;
    endOffset = selection.focus.offset;
  }
  // else: Middle node in multi-node selection - use full range (0 to textLength)

  // Validate offsets
  if (startOffset < 0 || endOffset > textLength || startOffset >= endOffset) {
    return false;
  }

  // If the entire node is selected, apply the style directly
  if (startOffset === 0 && endOffset === text.length) {
    const existingStyle = $isStyledTextNode(node) ? node.getRichStyle() : {};
    const newStyle = { ...existingStyle, [styleProperty]: styleValue };

    if ($isStyledTextNode(node)) {
      const writable = node.getWritable();
      writable.__richStyle = newStyle;
    } else {
      $convertToStyledTextNode(node, newStyle);
    }
    return true;
  }

  // Partial selection - need to split the node
  const beforeText = text.slice(0, startOffset);
  const selectedText = text.slice(startOffset, endOffset);
  const afterText = text.slice(endOffset);

  const existingStyle = $isStyledTextNode(node) ? node.getRichStyle() : {};
  const format = node.getFormat();
  const detail = node.getDetail();
  const mode = node.getMode();

  // Build array of new nodes to replace the original
  const newNodes = [];

  // Create the before node if needed
  if (beforeText) {
    const beforeNode = $isStyledTextNode(node)
      ? $createStyledTextNode(beforeText, existingStyle)
      : $createTextNode(beforeText);
    beforeNode.setFormat(format);
    beforeNode.setDetail(detail);
    beforeNode.setMode(mode);
    newNodes.push(beforeNode);
  }

  // Create the styled selected node
  const newStyle = { ...existingStyle, [styleProperty]: styleValue };
  const selectedNode = $createStyledTextNode(selectedText, newStyle);
  selectedNode.setFormat(format);
  selectedNode.setDetail(detail);
  selectedNode.setMode(mode);
  newNodes.push(selectedNode);

  // Create the after node if needed
  if (afterText) {
    const afterNode = $isStyledTextNode(node)
      ? $createStyledTextNode(afterText, existingStyle)
      : $createTextNode(afterText);
    afterNode.setFormat(format);
    afterNode.setDetail(detail);
    afterNode.setMode(mode);
    newNodes.push(afterNode);
  }

  // Get the parent to insert nodes correctly
  const parent = node.getParent();
  if (!parent) {
    return false;
  }

  // Insert all new nodes in order before removing the original
  newNodes.forEach((newNode) => {
    node.insertBefore(newNode);
  });

  // Remove the original node
  node.remove();

  return true;
}

/**
 * Get all text nodes from selection
 * Only returns nodes that are actually text nodes - doesn't expand element nodes
 */
function $getTextNodesFromSelection(selection) {
  const nodes = selection.getNodes();

  const textNodes = [];
  const seenKeys = new Set(); // Avoid duplicates

  // IMPORTANT: Only process nodes that are actually TextNodes
  // Do NOT recursively expand element nodes, as selection.getNodes()
  // already returns the correct set of nodes within the selection range
  nodes.forEach((node) => {
    if ($isTextNode(node)) {
      const key = node.getKey();
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        textNodes.push(node);
      }
    }
  });

  return textNodes;
}

/**
 * Apply a style to the current selection
 */
export function $applyStyle(styleProperty, styleValue) {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.isCollapsed()) {
    return false;
  }

  // Get all text nodes from the selection (handles element nodes too)
  const textNodes = $getTextNodesFromSelection(selection);

  if (textNodes.length === 0) {
    return false;
  }

  // Pre-calculate which nodes are anchor/focus and their offsets
  // BEFORE we start modifying nodes (which invalidates the selection)
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();

  // For anchor/focus, we want to use the actual text nodes if available
  // selection.anchor/focus.getNode() already returns the correct node
  const anchorKey = $isTextNode(anchorNode) ? anchorNode.getKey() : null;
  const focusKey = $isTextNode(focusNode) ? focusNode.getKey() : null;
  const anchorOffset = selection.anchor.offset;
  const focusOffset = selection.focus.offset;
  const isBackward = selection.isBackward();

  // Store the parent of the first node so we can restore selection later
  const firstNodeParent = textNodes[0]?.getParent();

  let success = false;
  let firstStyledNode = null;
  let lastStyledNode = null;
  let firstStyledOffset = 0;
  let lastStyledOffset = 0;

  // Process each text node with pre-calculated selection info
  textNodes.forEach((node, index) => {
    const nodeKey = node.getKey();
    const isAnchorNode = anchorKey ? nodeKey === anchorKey : false;
    const isFocusNode = focusKey ? nodeKey === focusKey : false;

    try {
      // Determine offsets for this node
      const textLength = node.getTextContent().length;

      // Skip empty nodes
      if (textLength === 0) {
        return;
      }

      let startOffset = 0;
      let endOffset = textLength;

      if (isAnchorNode && isFocusNode) {
        // Single node selection - use the actual offsets
        startOffset = Math.min(anchorOffset, focusOffset);
        endOffset = Math.max(anchorOffset, focusOffset);
      } else if (isBackward) {
        // Backward selection: anchor is at END, focus is at START
        if (isAnchorNode && anchorKey) {
          // This is the last node (anchor is at the end)
          startOffset = 0;
          endOffset = anchorOffset;
        } else if (isFocusNode && focusKey) {
          // This is the first node (focus is at the start)
          startOffset = focusOffset;
          endOffset = textLength;
        }
        // else: Middle node - use full range
      } else {
        // Forward selection: anchor is at START, focus is at END
        if (isAnchorNode && anchorKey) {
          // This is the first node
          startOffset = anchorOffset;
          endOffset = textLength;
        } else if (isFocusNode && focusKey) {
          // This is the last node
          startOffset = 0;
          endOffset = focusOffset;
        }
        // else: Middle node - use full range
      }

      // Skip if no actual text is selected in this node
      if (startOffset >= endOffset) {
        return;
      }

      // Store selection info for first and last nodes
      if (index === 0) {
        firstStyledOffset = startOffset;
      }
      if (index === textNodes.length - 1) {
        lastStyledOffset = endOffset;
      }

      // Apply style with calculated offsets
      const styledNode = $applyStyleToNodeWithOffsets(
        node,
        startOffset,
        endOffset,
        styleProperty,
        styleValue
      );
      if (styledNode) {
        success = true;

        // Track first and last styled nodes for selection restoration
        if (!firstStyledNode) {
          firstStyledNode = styledNode;
        }
        lastStyledNode = styledNode;
      }
    } catch (error) {
      console.error(`Error styling node ${index + 1}:`, error);
    }
  });

  // Restore selection to the styled text
  if (success && firstStyledNode && lastStyledNode) {
    try {
      // Select from start of first styled node to end of last styled node
      const newSelection = $getSelection();
      if ($isRangeSelection(newSelection)) {
        const firstLength = firstStyledNode.getTextContent().length;
        const lastLength = lastStyledNode.getTextContent().length;

        // Select the entire styled range
        newSelection.anchor.set(firstStyledNode.getKey(), 0, 'text');
        newSelection.focus.set(lastStyledNode.getKey(), lastLength, 'text');
      }
    } catch (error) {
      // Failed to restore selection
    }
  }

  return success;
}

/**
 * Remove a style property from the current selection
 */
export function $removeStyle(styleProperty) {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  // Get all text nodes from the selection (handles element nodes too)
  const textNodes = $getTextNodesFromSelection(selection);
  let success = false;

  textNodes.forEach((node) => {
    if ($isStyledTextNode(node)) {
      const style = node.getRichStyle();
      if (style[styleProperty]) {
        const newStyle = { ...style };
        delete newStyle[styleProperty];

        const writable = node.getWritable();

        // If no styles left, convert back to regular TextNode
        if (Object.keys(newStyle).length === 0) {
          const textNode = $createTextNode(node.getTextContent());
          textNode.setFormat(node.getFormat());
          textNode.setDetail(node.getDetail());
          textNode.setMode(node.getMode());
          node.replace(textNode);
        } else {
          writable.__richStyle = newStyle;
        }
        success = true;
      }
    }
  });

  return success;
}

/**
 * Toggle a style property in the current selection
 */
export function $toggleStyle(styleProperty, styleValue) {
  const hasStyle = $hasStyleInSelection(styleProperty);

  if (hasStyle) {
    return $removeStyle(styleProperty);
  }

  return $applyStyle(styleProperty, styleValue);
}

// Specific helper functions for each style type
export function $applyColor(color) {
  return $applyStyle('color', color);
}

export function $removeColor() {
  return $removeStyle('color');
}

export function $applyHighlight(color) {
  return $applyStyle('backgroundColor', color);
}

export function $removeHighlight() {
  return $removeStyle('backgroundColor');
}

export function $applyFontFamily(fontFamily) {
  return $applyStyle('fontFamily', fontFamily);
}

export function $removeFontFamily() {
  return $removeStyle('fontFamily');
}

export function $applyFontSize(fontSize) {
  return $applyStyle('fontSize', fontSize);
}

export function $removeFontSize() {
  return $removeStyle('fontSize');
}

// Getters for specific styles
export function $getColorInSelection() {
  return $getMostCommonStyleValue('color');
}

export function $getHighlightColorInSelection() {
  return $getMostCommonStyleValue('backgroundColor');
}

export function $getFontFamilyInSelection() {
  return $getMostCommonStyleValue('fontFamily');
}

export function $getFontSizeInSelection() {
  return $getMostCommonStyleValue('fontSize');
}
